import { ref, computed, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import apiService from '@/services/api'

// Define types
export interface DeliveryFeeRule {
  id?: number;
  name: string;
  description: string;
  rule_type: string;
  priority: number;
  is_active: boolean;
  rule_config: Record<string, any>;
}

export interface RuleCondition {
  type: string;
  min_order_total?: number;
  day?: string;
}

const supportedRuleTypes = [
  'order_total_threshold',
  'day_of_week',
  'flat_rate',
  'compound_and',
  'compound_or'
] as const

const supportedDays = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday'
] as const

export function useDeliveryFeeRules() {
  const toast = useToast()

  // State
  const rules = ref<DeliveryFeeRule[]>([])
  const loading = ref(false)
  const saving = ref(false)

  // Modal states
  const showCreateModal = ref(false)
  const showEditModal = ref(false)
  const showDeleteModal = ref(false)

  // Current rule being edited/created
  const currentRule = ref<DeliveryFeeRule>({
    name: '',
    description: '',
    rule_type: '',
    priority: 100,
    is_active: true,
    rule_config: {}
  })

  // Rule to delete
  const ruleToDelete = ref<DeliveryFeeRule | null>(null)

  // Computed
  const sortedRules = computed(() => {
    if (!rules.value || !Array.isArray(rules.value)) return []
    return [...rules.value].sort((a, b) => a.priority - b.priority)
  })

  // Methods
  const loadRules = async () => {
    loading.value = true
    try {
      const response = await apiService.get('/delivery-fee/rules')
      rules.value = (response as unknown as DeliveryFeeRule[]) || []
    } catch (error) {
      console.error('Error loading delivery fee rules:', error)
      toast.error('Error al cargar las reglas de envío')
      rules.value = []
    } finally {
      loading.value = false
    }
  }

  const onRuleTypeChange = () => {
    // Reset rule config when type changes
    if (currentRule.value.rule_type === 'compound_and' || currentRule.value.rule_type === 'compound_or') {
      currentRule.value.rule_config = {
        conditions: [],
        fee: 0
      }
    } else {
      currentRule.value.rule_config = {}
    }
  }

  const addCondition = () => {
    if (!currentRule.value.rule_config.conditions) {
      currentRule.value.rule_config.conditions = []
    }
    currentRule.value.rule_config.conditions.push({
      type: 'order_total_threshold',
      min_order_total: 0
    })
  }

  const removeCondition = (index: number) => {
    if (currentRule.value.rule_config.conditions) {
      currentRule.value.rule_config.conditions.splice(index, 1)
    }
  }

  const resetCurrentRule = () => {
    currentRule.value = {
      name: '',
      description: '',
      rule_type: '',
      priority: 100,
      is_active: true,
      rule_config: {}
    }
  }

  const closeModal = () => {
    showCreateModal.value = false
    showEditModal.value = false
    resetCurrentRule()
  }

  const openCreateModal = () => {
    resetCurrentRule()
    showEditModal.value = false
    showCreateModal.value = true
  }

  const editRule = (rule: DeliveryFeeRule) => {
    currentRule.value = {
      ...rule,
      rule_config: { ...rule.rule_config }
    }
    showEditModal.value = true
  }

  const isNonNegativeNumber = (value: unknown): boolean => {
    return typeof value === 'number' && Number.isFinite(value) && value >= 0
  }

  const validateCondition = (condition: RuleCondition, index: number): boolean => {
    if (!condition?.type) {
      toast.error(`La condición #${index + 1} debe tener un tipo válido`)
      return false
    }

    if (condition.type === 'order_total_threshold') {
      if (!isNonNegativeNumber(condition.min_order_total)) {
        toast.error(`La condición #${index + 1} debe tener un total mínimo válido (>= 0)`)
        return false
      }
      return true
    }

    if (condition.type === 'day_of_week') {
      if (!condition.day || !supportedDays.includes(condition.day as any)) {
        toast.error(`La condición #${index + 1} debe tener un día válido`)
        return false
      }
      return true
    }

    toast.error(`La condición #${index + 1} tiene un tipo no soportado`)
    return false
  }

  const validateRuleForm = (): boolean => {
    currentRule.value.name = currentRule.value.name?.trim?.() || ''
    currentRule.value.description = currentRule.value.description?.trim?.() || ''

    if (!currentRule.value.name) {
      toast.error('El nombre de la regla es obligatorio')
      return false
    }

    if (!supportedRuleTypes.includes(currentRule.value.rule_type as any)) {
      toast.error('Selecciona un tipo de regla válido')
      return false
    }

    const priority = currentRule.value.priority
    if (!Number.isInteger(priority) || priority < 1 || priority > 1000) {
      toast.error('La prioridad debe ser un número entero entre 1 y 1000')
      return false
    }

    const config = currentRule.value.rule_config || {}

    if (currentRule.value.rule_type === 'order_total_threshold') {
      if (!isNonNegativeNumber(config.min_order_total)) {
        toast.error('El total mínimo de compra debe ser un número válido (>= 0)')
        return false
      }

      if (!isNonNegativeNumber(config.fee_above_threshold)) {
        toast.error('El costo de envío para total mayor o igual debe ser válido (>= 0)')
        return false
      }

      if (!isNonNegativeNumber(config.fee_below_threshold)) {
        toast.error('El costo de envío para total menor debe ser válido (>= 0)')
        return false
      }
    }

    if (currentRule.value.rule_type === 'day_of_week') {
      if (!config.day || !supportedDays.includes(config.day as any)) {
        toast.error('Selecciona un día de la semana válido')
        return false
      }

      if (!isNonNegativeNumber(config.fee)) {
        toast.error('El costo de envío debe ser un número válido (>= 0)')
        return false
      }
    }

    if (currentRule.value.rule_type === 'flat_rate') {
      if (!isNonNegativeNumber(config.fee)) {
        toast.error('La tarifa plana debe ser un número válido (>= 0)')
        return false
      }
    }

    if (currentRule.value.rule_type === 'compound_and' || currentRule.value.rule_type === 'compound_or') {
      if (!isNonNegativeNumber(config.fee)) {
        toast.error('El costo de envío de la regla combinada debe ser válido (>= 0)')
        return false
      }

      if (!Array.isArray(config.conditions) || config.conditions.length === 0) {
        toast.error('La regla combinada debe tener al menos una condición')
        return false
      }

      for (let i = 0; i < config.conditions.length; i += 1) {
        if (!validateCondition(config.conditions[i], i)) {
          return false
        }
      }
    }

    return true
  }

  const saveRule = async () => {
    if (!validateRuleForm()) {
      return
    }

    saving.value = true
    try {
      if (showEditModal.value) {
        await apiService.put(`/delivery-fee/rules/${currentRule.value.id}`, currentRule.value)
        toast.success('Regla actualizada correctamente')
      } else {
        await apiService.post('/delivery-fee/rules', currentRule.value)
        toast.success('Regla creada correctamente')
      }

      await loadRules()
      closeModal()
    } catch (error) {
      console.error('Error saving delivery fee rule:', error)
      toast.error('Error al guardar la regla de envío')
    } finally {
      saving.value = false
    }
  }

  const toggleRuleStatus = async (rule: DeliveryFeeRule) => {
    try {
      await apiService.put(`/delivery-fee/rules/${rule.id}`, {
        ...rule,
        is_active: !rule.is_active
      })
      await loadRules()
      toast.success(`Regla ${rule.is_active ? 'desactivada' : 'activada'} correctamente`)
    } catch (error) {
      console.error('Error toggling rule status:', error)
      toast.error('Error al cambiar el estado de la regla')
    }
  }

  const deleteRule = (rule: DeliveryFeeRule) => {
    ruleToDelete.value = rule
    showDeleteModal.value = true
  }

  const confirmDelete = async () => {
    try {
      if (ruleToDelete.value?.id) {
        await apiService.delete(`/delivery-fee/rules/${ruleToDelete.value.id}`)
        await loadRules()
        showDeleteModal.value = false
        ruleToDelete.value = null
        toast.success('Regla eliminada correctamente')
      }
    } catch (error) {
      console.error('Error deleting delivery fee rule:', error)
      toast.error('Error al eliminar la regla de envío')
    }
  }

  const getRuleTypeLabel = (type: string): string => {
    const labels = {
      'order_total_threshold': 'Umbral de Total',
      'day_of_week': 'Día de Semana',
      'flat_rate': 'Tarifa Plana',
      'distance_based': 'Basado en Distancia',
      'time_based': 'Basado en Hora',
      'compound_and': 'Regla Combinada (Y)',
      'compound_or': 'Regla Combinada (O)'
    }
    return labels[type as keyof typeof labels] || type
  }

  const formatRuleConfig = (type: string, config: Record<string, any>): string => {
    try {
      switch (type) {
        case 'order_total_threshold':
          return `Si el total ≥ $${config.min_order_total?.toFixed(2)}: $${config.fee_above_threshold?.toFixed(2)}, si no: $${config.fee_below_threshold?.toFixed(2)}`
        case 'day_of_week':
          const dayLabels = {
            'monday': 'Lunes',
            'tuesday': 'Martes',
            'wednesday': 'Miércoles',
            'thursday': 'Jueves',
            'friday': 'Viernes',
            'saturday': 'Sábado',
            'sunday': 'Domingo'
          }
          return `Los ${dayLabels[config.day as keyof typeof dayLabels]}: $${config.fee?.toFixed(2)}`
        case 'flat_rate':
          return `Tarifa fija: $${config.fee?.toFixed(2)}`
        case 'compound_and':
        case 'compound_or':
          const operator = type === 'compound_and' ? 'Y' : 'O'

          if (!config.conditions || !Array.isArray(config.conditions)) {
            return 'Configuración inválida'
          }

          const conditionTexts = config.conditions.map((condition: any) => {
            const conditionType = condition.type

            switch (conditionType) {
              case 'day_of_week':
                const dayLabels = {
                  'monday': 'Lunes',
                  'tuesday': 'Martes',
                  'wednesday': 'Miércoles',
                  'thursday': 'Jueves',
                  'friday': 'Viernes',
                  'saturday': 'Sábado',
                  'sunday': 'Domingo'
                }
                const day = dayLabels[condition.day as keyof typeof dayLabels] || condition.day
                return `Día = ${day}`
              case 'order_total_threshold':
                return `Total ≥ $${condition.min_order_total?.toFixed(2)}`
              default:
                return conditionType
            }
          })

          const result = `Si (${conditionTexts.join(` ${operator} `)}): $${config.fee?.toFixed(2)}`
          return result
        default:
          return type
      }
    } catch (error) {
      return 'Error en configuración'
    }
  }

  onMounted(() => {
    loadRules()
  })

  return {
    // State
    rules,
    loading,
    saving,
    showCreateModal,
    showEditModal,
    showDeleteModal,
    currentRule,
    ruleToDelete,
    sortedRules,
    
    // Methods
    loadRules,
    onRuleTypeChange,
    addCondition,
    removeCondition,
    resetCurrentRule,
    closeModal,
    openCreateModal,
    editRule,
    saveRule,
    toggleRuleStatus,
    deleteRule,
    confirmDelete,
    getRuleTypeLabel,
    formatRuleConfig,
    
    // Constants
    supportedRuleTypes,
    supportedDays
  }
}
